import * as Toggle from '@radix-ui/react-toggle-group'
import { TogglePrimitiveVisibilityButton } from 'features/kestrelViewer/kestrelViewerMarkup/primitives/TogglePrimitiveVisibilityButton'
import {
  ActionsContainer,
  DraggableWrapper,
  DropIndicator,
  LeftSideInfo,
  PrimaryLineContainer,
  useDragHandlers,
} from 'features/kestrelViewer/kestrelViewerMarkup/utils/DragAndDropComponents'
import { useEntryBehavior } from 'features/kestrelViewer/kestrelViewerMarkup/utils/useEntryBehavior'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { getPrimitiveGeometryDisplayText, KVPrimitive } from 'types'

import { DeletePrimitiveButton } from './DeletePrimitiveButton'
import { EditPrimitiveButton } from './EditPrimitiveButton'

const StyledPrimitiveEntry = styled(Toggle.Item)<{ $selected: boolean }>`
  all: unset;
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 4px;

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const PrimitiveLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`

const PrimitiveMetadata = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.text2};
`

export const PrimitiveEntry: FC<{
  primitive: KVPrimitive
  selectedMarkupId: string | undefined
  onReorder?: (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => void
}> = ({ primitive, onReorder }) => {
  const [isHovered, setIsHovered] = useState(false)

  const { isSelected, handleClick, handleHover, handleHoverEnd } =
    useEntryBehavior(
      primitive,
      KestrelCommands.HIGHLIGHT_SURFACE_AREA, // TODO: Add HIGHLIGHT_PRIMITIVE command
      KestrelCommands.UNHIGHLIGHT_ALL_PRIMITIVES,
      'primitiveId'
    )

  const {
    dropPosition,
    isDragging,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragHandlers(primitive, onReorder || (() => {}), 'primitiveid')

  const geometryText = getPrimitiveGeometryDisplayText(primitive.geometry)

  return (
    <StyledPrimitiveEntry
      value={primitive.id}
      $selected={isSelected}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => {
        setIsHovered(true)
        handleHover()
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        handleHoverEnd()
      }}
      onClick={handleClick}>
      {dropPosition && <DropIndicator $position={dropPosition} />}
      <DraggableWrapper
        $isDragging={isDragging}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        <PrimaryLineContainer $visible={primitive.visible}>
          <LeftSideInfo>
            <PrimitiveLabel>{primitive.label}</PrimitiveLabel>
          </LeftSideInfo>
          {!isHovered && <PrimitiveMetadata>{geometryText}</PrimitiveMetadata>}
          {isHovered && (
            <ActionsContainer>
              <EditPrimitiveButton primitive={primitive} />
              <TogglePrimitiveVisibilityButton primitive={primitive} />
              <DeletePrimitiveButton primitiveId={primitive.id} />
            </ActionsContainer>
          )}
        </PrimaryLineContainer>
        {isSelected && primitive.notes && (
          <div style={{ color: 'lightgray', fontStyle: 'italic' }}>
            {primitive.notes}
          </div>
        )}
      </DraggableWrapper>
    </StyledPrimitiveEntry>
  )
}
